import { createContext } from 'react';
import i18next from 'i18next';
import yup from 'validation';
import { ECountryCode, ELanguageCode, EOfferVersionContentBlockFormFieldType, EOfferVersionContentBlockLineItemSource, EOfferVersionContentBlockType } from '__generated__/graphql';
import { QuickPriceCalculatorTypeLister } from 'semshared/pricelist/quickprice_lister';
import { QuickPriceCalculatorTypeWidget } from 'semshared/pricelist/quickprice_widget';

export const taxesSchema = yup.object().shape({
  taxTypeId: yup.number().required(),
  taxRateId: yup.number().required(),
  name: yup.string().required(),
  rate: yup.number().required(),
  price: yup.number().required(),
});

const componentsSchemaBase = yup.object().shape({
  taxTypeId: yup.number().required(),
  name: yup.string().required(),
  price: yup.number().required().label('offerblock-lineitem-priceitem'),
});

export const componentsSchemaLister = componentsSchemaBase.shape({
  price: yup.number().required().min(0).label('offerblock-lineitem-priceitem'),
});

export const componentsSchemaWidget = componentsSchemaBase.shape({});

export const invoiceSchema = yup.object().shape({
  commission: yup.boolean().nullable(),
});

/** Booker request matched to one seminar-room line. Absent means the hotel added the room. */
export type SeminarRoomRequest = {
  occupancyFrom?: string | null;
  occupancyTo?: string | null;
  seating?: string | null;
  seatCount?: number | null;
  requireAirConditioning?: boolean | null;
  requireDaylight?: boolean | null;
  minRoomAreaSqm?: number | null;
  beamerCount?: number | null;
  flipchartCount?: number | null;
  pinboardCount?: number | null;
};

export type SeminarRoomValidation = {
  enforce: boolean;
  isSeminarRoom: (sku?: string | null) => boolean;
  requestAt: (day: number, seminarIndex: number) => SeminarRoomRequest | null | undefined;
};

/** i18next.t can return null; Yup only accepts a string message. */
const validationMessage = (key: string, options?: { size: number }) =>
  String(options ? i18next.t(key, options) : i18next.t(key));

const seminarRoomConfirmationTest = function (this: yup.TestContext, value: any) {
  const validation = (this.options.context as { seminarRoomValidationRef?: { current: SeminarRoomValidation } } | undefined)?.seminarRoomValidationRef?.current;
  if (!validation?.enforce || !value || !validation.isSeminarRoom(value.sku)) return true;

  const ancestors = ((this as yup.TestContext & { from?: { value?: { lineItems?: unknown[] } }[] }).from || []).map(entry => entry.value);
  const block = ancestors.find(entry => Array.isArray(entry?.lineItems));
  const items = (Array.isArray(this.parent) ? this.parent : block?.lineItems) || [];
  let seminarIndex = 0;
  for (const item of items) {
    if (item === value) break;
    if (validation.isSeminarRoom((item as { sku?: string | null })?.sku)) seminarIndex += 1;
  }
  const request = validation.requestAt(value.day, seminarIndex);
  const minimum = request?.minRoomAreaSqm || 0;
  const size = value.guaranteedRoomAreaSqm;
  if (minimum > 0 && (size == null || !Number.isInteger(Number(size)) || Number(size) < minimum)) {
    return this.createError({
      path: `${this.path}.guaranteedRoomAreaSqm`,
      message: validationMessage('offer-room-size-min', { size: minimum }),
    });
  }
  if (request?.requireAirConditioning && value.confirmAirConditioning !== true) {
    return this.createError({
      path: `${this.path}.confirmAirConditioning`,
      message: validationMessage('offer-room-confirm-required'),
    });
  }
  if (request?.requireDaylight && value.confirmDaylight !== true) {
    return this.createError({
      path: `${this.path}.confirmDaylight`,
      message: validationMessage('offer-room-confirm-required'),
    });
  }
  const requestedCharacteristic = !!request && (request.requireAirConditioning || request.requireDaylight || minimum > 0);
  if ((requestedCharacteristic || !request) && value.requirementsAcknowledged !== true) {
    return this.createError({
      path: `${this.path}.requirementsAcknowledged`,
      message: validationMessage('offer-room-acknowledge-required'),
    });
  }
  return true;
};

const lineItemSchemaBase = yup.object().shape({
  id: yup.number().nullable(),
  day: yup.number().required(),
  count: yup.number().required().min(0).label('offerblock-lineitem-count'),
  sku: yup.string().nullable(),
  spaceId: yup.number().nullable(),
  header: yup.string().required().label('offerblock-lineitem-header'),
  details: yup.string(),
  priceItem: yup.number().required().label('offerblock-lineitem-priceitem'),
  priceGross: yup.number().required().label('offerblock-lineitem-pricegross'),
  priceNet: yup.number().required().label('offerblock-lineitem-pricenet'),
  sequence: yup.number().required(),
  invoice: invoiceSchema.nullable().optional(),
  components: yup.array().required().of(componentsSchemaBase),
  taxes: yup.array().required().of(taxesSchema),
  source: yup.mixed<EOfferVersionContentBlockLineItemSource>().required(),
  guaranteedRoomAreaSqm: yup.number().nullable().integer(),
  confirmAirConditioning: yup.boolean().nullable(),
  confirmDaylight: yup.boolean().nullable(),
  requirementsAcknowledged: yup.boolean().nullable(),
  offeredSeatCount: yup.number().nullable().integer(),
  offeredFrom: yup.string().nullable(),
  offeredTo: yup.string().nullable(),
  offeredSeating: yup.string().nullable(),
  offeredBeamerCount: yup.number().nullable().integer(),
  offeredFlipchartCount: yup.number().nullable().integer(),
  offeredPinboardCount: yup.number().nullable().integer(),
}).test('seminar-room-confirmation', seminarRoomConfirmationTest)

export const lineItemSchemaLister = lineItemSchemaBase.shape({
  priceItem: yup.number().required().min(0).label('offerblock-lineitem-priceitem'),
  priceGross: yup.number().required().min(0).label('offerblock-lineitem-pricegross'),
  priceNet: yup.number().required().min(0).label('offerblock-lineitem-pricenet'),
  components: yup.array().required().of(componentsSchemaLister),
});

export const lineItemSchemaWidget = lineItemSchemaBase.shape({
  components: yup.array().required().of(componentsSchemaWidget),
});

export const formFieldsSchema = yup.object().shape({
  id: yup.number().required(),
  type: yup.mixed<EOfferVersionContentBlockFormFieldType>().required(),
  name: yup.string().required(),
  label: yup.string().required(),
  placeholder: yup.string().nullable(),
  sequence: yup.number().required(),
  required: yup.boolean().nullable(),
  value: yup.string().nullable(),
});

const contentBlockSchemaBase = yup.object().shape({
  sequence: yup.number().required(),
  template: yup
    .string()
    .required()
    .when('type', {
      is: 'PLAIN_HTML',
      then: schema => schema.label('offerblock-plainhtml-html'),
    })
    .when('type', {
      is: 'INPUT_DAYS',
      then: schema => schema.label('offerblock-inputdays-html'),
    })
    .when('type', {
      is: 'LINE_ITEMS',
      then: schema => schema.label('offerblock-lineitems-html'),
    })
    .when('type', {
      is: 'CANCELLATION_ITEMS',
      then: schema => schema.label('offerblock-lineitems-html'),
    })
    .when('type', {
      is: 'CUSTOM_FORM',
      then: schema => schema.label('offerblock-customform-html'),
    }),
  type: yup.mixed<EOfferVersionContentBlockType>().required(),
  day: yup.number().nullable(),
  blockDescription: yup.string().nullable(),
  formFields: yup.array().of(formFieldsSchema),
  lineItems: yup.array().of(lineItemSchemaBase),
  cancellationRule: yup.object({
    daysToEvent: yup.number().required(),
    rate: yup.number().required(),
  }).nullable().notRequired().default(undefined),
});

export const contentBlockSchemaLister = contentBlockSchemaBase.shape({
  lineItems: yup.array().of(lineItemSchemaLister),
});

export const contentBlockSchemaWidget = contentBlockSchemaBase.shape({
  lineItems: yup.array().of(lineItemSchemaWidget),
});

export const clientSchema = yup.object().shape({
  email: yup.string().required().label('offertemplate-client-email'),
  phone: yup.string().nullable().label('offertemplate-client-phone'),
  firstname: yup.string().required().label('offertemplate-client-company-firstname'),
  lastname: yup.string().required().label('offertemplate-client-company-lastname'),
  company: yup.string().required().label('offertemplate-client-company-company'),
  country: yup.mixed<ECountryCode>().oneOf(Object.values(ECountryCode)).required().label('offertemplate-client-company-country'),
  address: yup.string().required().label('offertemplate-client-company-address'),
  city: yup.string().required().label('offertemplate-client-company-city'),
  zip: yup.string().required().label('offertemplate-client-company-zip'),
  billingCompany: yup.string().required().label('offertemplate-client-billing-company'),
  billingFirstname: yup.string().required().label('offertemplate-client-billing-firstname'),
  billingLastname: yup.string().required().label('offertemplate-client-billing-lastname'),
  billingAddress: yup.string().required().label('offertemplate-client-billing-address'),
  billingCity: yup.string().required().label('offertemplate-client-billing-city'),
  billingZip: yup.string().required().label('offertemplate-client-billing-zip'),
  billingCountry: yup.mixed<ECountryCode>().oneOf(Object.values(ECountryCode)).required().label('offertemplate-client-billing-country'),
});

/** SEM can save and send an inquiry with only an email; both address blocks stay optional. Empty strings stay valid so the API still receives strings. Country keeps a default. */
export const clientSchemaWidget = clientSchema.shape({
  firstname: yup.string().defined().label('offertemplate-client-company-firstname'),
  lastname: yup.string().defined().label('offertemplate-client-company-lastname'),
  company: yup.string().defined().label('offertemplate-client-company-company'),
  address: yup.string().defined().label('offertemplate-client-company-address'),
  city: yup.string().defined().label('offertemplate-client-company-city'),
  zip: yup.string().defined().label('offertemplate-client-company-zip'),
  billingCompany: yup.string().defined().label('offertemplate-client-billing-company'),
  billingFirstname: yup.string().defined().label('offertemplate-client-billing-firstname'),
  billingLastname: yup.string().defined().label('offertemplate-client-billing-lastname'),
  billingAddress: yup.string().defined().label('offertemplate-client-billing-address'),
  billingCity: yup.string().defined().label('offertemplate-client-billing-city'),
  billingZip: yup.string().defined().label('offertemplate-client-billing-zip'),
});

const validationSchemaBase = yup.object().shape({
  id: yup.number().required(),
  title: yup.string().nullable(),
  filename: yup.string().nullable(),
  version: yup.string().required().label('offertemplate-version'),
  offerDate: yup.date().required().label('offertemplate-offerdate'),
  startDate: yup.string().matches(/^\d{4}-\d{2}-\d{2}$/).required().label('offertemplate-startdate'),
  pageOptions: yup.object().shape({
    marginTop: yup.number().required().label('offertemplate-marginTop'),
    marginBottom: yup.number().required().label('offertemplate-marginBottom'),
    marginLeft: yup.number().required().label('offertemplate-marginLeft'),
    marginRight: yup.number().required().label('offertemplate-marginRight'),
    cssStyles: yup.string().nullable(),
    headerTemplate: yup.string().nullable(),
    footerTemplate: yup.string().nullable(),
  }),
  includeEmptyLineItems: yup.boolean().required(),
  contentBlocks: yup.array().required().min(1).of(contentBlockSchemaBase).label('offertemplate-layout'),
  totalPriceNet: yup.number().required(),
  totalPriceGross: yup.number().required(),
  totalTax: yup.number().required(),
  totalTaxes: yup.array().required().of(taxesSchema),
  additionalInfo1: yup.string().nullable(),
  additionalInfo2: yup.string().nullable(),
  additionalInfo3: yup.string().nullable(),
  personalMessage: yup.string().nullable(),
  agreedCancellationWaiveNet: yup.number().nullable(),
  manualCancellationWaiveNet: yup.number().nullable(),
  client: clientSchema.nullable(),
  _documentChangeTimestamp: yup.number().optional(),
});

export const validationSchemaLister = validationSchemaBase.shape({
  contentBlocks: yup.array().required().min(1).of(contentBlockSchemaLister).label('offertemplate-layout'),
});

export const validationSchemaWidget = validationSchemaBase.shape({
  contentBlocks: yup.array().required().min(1).of(contentBlockSchemaWidget).label('offertemplate-layout'),
  client: clientSchemaWidget.nullable(),
});

export type OfferVersionFormType = yup.InferType<typeof validationSchemaLister> | yup.InferType<typeof validationSchemaWidget>;
export type ContentBlockFormType = yup.InferType<typeof contentBlockSchemaBase> | yup.InferType<typeof contentBlockSchemaLister> | yup.InferType<typeof contentBlockSchemaWidget>;
export type LineItemFormType = yup.InferType<typeof lineItemSchemaBase> | yup.InferType<typeof lineItemSchemaLister> | yup.InferType<typeof lineItemSchemaWidget>;
export type FormFieldsFormType = yup.InferType<typeof formFieldsSchema>;
export type TaxesFormType = yup.InferType<typeof taxesSchema>;

export const cancellationRuleSchema = yup.object().shape({
  id: yup.number().nullable(),
  daysToEvent: yup.number().required().label('offer-info-cancellation-rule-days'),
  minOvernight: yup.number().nullable().label('hotel-cancellation-details-cancellation-overnight'),
  maxOvernight: yup.number().nullable(),
  minTotalGuests: yup.number().nullable().label('hotel-cancellation-details-cancellation-totalguests'),
  maxTotalGuests: yup.number().nullable(),
  rate: yup.number().required().label('offer-info-cancellation-rule-rate').transform((value) => Number.isNaN(value) ? null : value),
});
export const cancellationRulesSchema = yup.object().shape({
  cancellationRules: yup.array().of(cancellationRuleSchema).required(),
  toleranceRate: yup.number().nullable().label('offer-info-cancellation-agreed-waiver').min(0).max(100).transform((value) => Number.isNaN(value) ? null : value),
});

/** Simple rule for category-specific list (days + rate only). */
export const cancellationRuleCategoryItemSchema = yup.object().shape({
  id: yup.number().nullable(),
  daysToEvent: yup.number().required().label('offer-info-cancellation-rule-days'),
  rate: yup.number().required().label('offer-info-cancellation-rule-rate').transform((value) => Number.isNaN(value) ? null : value),
});
export const cancellationRulesTwoCategorySchema = yup.object().shape({
  toleranceRate: yup.number().nullable().label('offer-info-cancellation-agreed-waiver').min(0).max(100).transform((value) => Number.isNaN(value) ? null : value),
  overnightRules: yup.array().of(cancellationRuleCategoryItemSchema).required(),
  seminarRules: yup.array().of(cancellationRuleCategoryItemSchema).required(),
});
/** Single list when no overnight/seminar categories (default / general conditions). */
export const cancellationRulesGeneralSchema = yup.object().shape({
  toleranceRate: yup.number().nullable().label('offer-info-cancellation-agreed-waiver').min(0).max(100).transform((value) => Number.isNaN(value) ? null : value),
  generalRules: yup.array().of(cancellationRuleCategoryItemSchema).required(),
});
export type CancellationRulesFormType = yup.InferType<typeof cancellationRulesSchema>;
export type CancellationRuleFormType = yup.InferType<typeof cancellationRuleSchema>;
export type CancellationRuleCategoryItemFormType = yup.InferType<typeof cancellationRuleCategoryItemSchema>;
export type CancellationRulesTwoCategoryFormType = yup.InferType<typeof cancellationRulesTwoCategorySchema>;
export type CancellationRulesGeneralFormType = yup.InferType<typeof cancellationRulesGeneralSchema>;

export const downpaymentSchema = yup.object().shape({
  downpaymentDueDate: yup.date().required().label('offer-info-downpayment-due-date'),
  downpaymentAmount: yup.number().required().label('offer-info-downpayment-amount').transform((value) => Number.isNaN(value) ? null : value),
  downpaymentRemark: yup.string().nullable().label('offer-info-downpayment-remark'),
});
export type DownpaymentFormType = yup.InferType<typeof downpaymentSchema>;

/**
 * Context providing quick price calculator for offer line item price calculations.
 * Different implementations for Lister (EPILO) and Widget (SEM) booking modes.
 */
export const QuickPriceCalculatorContext = createContext<QuickPriceCalculatorTypeLister | QuickPriceCalculatorTypeWidget | null>(null);
